import { StringIndexedObject } from '@kanda-libs/ks-component-ts';
import { Price, Text } from '@kanda-libs/ks-design-library';

import CompanyCell from 'components/cell/CompanyCell';
import CompanyRisk from 'components/company/CompanyTable/CompanyRisk';
import { Activity } from 'components/job/JobTable/Activity';
import { CreatedAt } from 'components/job/JobTable/CreatedAt';
import Customer from 'components/job/JobTable/Customer';
import Job from 'components/job/JobTable/Job';
import JobBankDetails from 'components/job/JobTable/JobBankDetails';
import JobIds from 'components/job/JobTable/JobIds';
import PriceStatus from 'components/job/JobTable/PriceStatus';

export const CLASS_NAMES = {
  table: '-mx-8',
};

export const FLAG_TABLE_COLUMN = {
  Header: 'Risk',
  accessor: 'companyRisk',
  subAccessors: ['cid'],
  items: [
    {
      action: {
        type: 'companyFlag-alphabetical',
      },
      icon: 'alphabet',
      name: 'Sort Flag',
    },
  ],
  renderComponent: {
    component: CompanyRisk,
    value: 'flag',
    optionalProps: ['cid'],
  },
  width: 45,
  minWidth: 45,
  maxWidth: 45,
};

export const TABLE_COLUMNS = [
  {
    Header: 'Job',
    accessor: 'title',
    subAccessors: ['id', 'archived'],
    items: [
      {
        action: {
          type: 'job-alphabetical',
        },
        icon: 'alphabet',
        name: 'Sort job title',
      },
      {
        action: {
          type: 'id-alphabetical',
        },
        icon: 'alphabet',
        name: 'Sort job ID',
      },
    ],
    renderComponent: {
      component: Job,
      value: 'title',
      optionalProps: ['id', 'archived'],
    },
    width: 180,
    minWidth: 100,
    maxWidth: 2000,
  },
  {
    Header: 'Price',
    accessor: 'price',
    subAccessors: ['status', 'financeStatus', 'quotedTo'],
    items: [
      {
        action: {
          type: 'price-ascending',
        },
        icon: 'sort-ascending',
        name: 'Sort ascending',
      },
      {
        action: {
          type: 'price-descending',
        },
        icon: 'sort-descending',
        name: 'Sort descending',
      },
    ],
    width: 180,
    renderComponent: {
      component: PriceStatus,
      value: 'total',
      optionalProps: ['status', 'financeStatus', 'quotedTo'],
    },
  },
  {
    Header: 'Customer',
    accessor: 'customerDetails',
    items: [
      {
        action: {
          type: 'name-alphabetical',
        },
        icon: 'alphabet',
        name: 'Sort customer name',
      },
    ],
    width: 240,
    renderComponent: {
      component: Customer,
      value: 'customerDetails',
    },
  },
  {
    Header: 'Company',
    accessor: 'companyName',
    items: [
      {
        action: {
          type: 'company-name-alphabetical',
        },
        icon: 'alphabet',
        name: 'Sort company name',
      },
    ],
    subAccessors: ['companyName', 'companyContactEmail', 'companyContactPhone'],
    width: 220,
    renderComponent: {
      component: CompanyCell,
      value: 'companyName',
      optionalProps: [
        'companyName',
        'companyContactEmail',
        'companyContactPhone',
      ],
    },
  },
  {
    Header: 'Last Activity',
    accessor: 'updatedAt',
    items: [
      {
        action: {
          type: 'activity-ascending',
        },
        icon: 'sort-ascending',
        name: 'Sort ascending',
      },
      {
        action: {
          type: 'activity-descending',
        },
        icon: 'sort-descending',
        name: 'Sort descending',
      },
    ],
    subAccessors: ['status', 'financeStatus', 'archived'],
    width: 180,
    renderComponent: {
      component: Activity,
      value: 'date',
      optionalProps: ['status', 'financeStatus', 'archived'],
    },
  },
  {
    Header: 'Created At',
    accessor: 'createdAt',
    items: [
      {
        action: {
          type: 'created-ascending',
        },
        icon: 'sort-ascending',
        name: 'Sort ascending',
      },
      {
        action: {
          type: 'created-descending',
        },
        icon: 'sort-descending',
        name: 'Sort descending',
      },
    ],
    width: 180,
    renderComponent: {
      component: CreatedAt,
      value: 'date',
    },
  },
];

export const PAYOUT_PENDING_COLUMNS = [
  {
    Header: 'IDs',
    accessor: 'id',
    subAccessors: ['reference'],
    renderComponent: {
      component: JobIds,
      value: 'id',
      optionalProps: ['reference'],
    },
    width: 240,
    minWidth: 200,
    maxWidth: 400,
  },
  {
    Header: 'Rate',
    accessor: 'rateName',
    renderComponent: {
      component: Text,
      value: 'text',
      props: {
        className: 'text-14-22',
      },
    },
    width: 120,
    minWidth: 100,
    maxWidth: 200,
  },
  {
    Header: 'Loan',
    accessor: 'loan',
    renderComponent: {
      component: Price,
      value: 'amount',
      props: {
        color: 'neutral-600',
        poundsClassName: 'text-14-22-em',
        centsClassName: 'text-14-22-em',
      },
    },
    width: 80,
    minWidth: 80,
    maxWidth: 150,
  },
  {
    Header: 'Deposit',
    accessor: 'deposit',
    renderComponent: {
      component: Price,
      value: 'amount',
      props: {
        color: 'neutral-600',
        poundsClassName: 'text-14-22-em',
        centsClassName: 'text-14-22-em',
      },
    },
    width: 80,
    minWidth: 80,
    maxWidth: 150,
  },
  {
    Header: 'TP Deposit',
    accessor: 'depositTaken',
    renderComponent: {
      component: Text,
      value: 'text',
      props: {
        className: 'text-14-22-em',
      },
    },
    width: 80,
    minWidth: 80,
    maxWidth: 150,
  },
  {
    Header: 'To Pay',
    accessor: 'toPay',
    renderComponent: {
      component: Price,
      value: 'amount',
      props: {
        color: 'neutral-600',
        poundsClassName: 'text-14-22-em',
        centsClassName: 'text-14-22-em',
      },
    },
    width: 80,
    minWidth: 80,
    maxWidth: 150,
  },
  {
    Header: 'Bank Details',
    accessor: 'bankDetails',
    width: 180,
    minWidth: 120,
    maxWidth: 200,
    renderComponent: {
      component: JobBankDetails,
      value: 'bankDetails',
    },
  },
];

export const DEFAULT_COLUMN = {
  minWidth: 80,
  width: 200,
  maxWidth: 400,
};

export const SORTING = {
  UPDATED_AT: 'UPDATED_AT',
  JOB: 'JOB',
  ID: 'ID',
  PRICE: 'PRICE',
  CUSTOMER: 'CUSTOMER',
  COMPANY: 'COMPANY',
  CREATED_AT: 'CREATED_AT',
};

export const ORDER = {
  NORMAL: 'NORMAL',
  INVERSE: 'INVERSE',
};

export const FILTER_TYPES = {
  NONE: 'NONE',
  STATUS: 'STATUS',
};

export const SEARCH_OPTIONS = {
  keys: [
    { name: 'customer.firstName', weight: 1 },
    { name: 'customer.lastName', weight: 1 },
    { name: 'customer.email', weight: 1 },
    { name: 'customer.phone', weight: 1 },
    { name: 'customer.postCode', weight: 1 },
    { name: 'customer.addressLine1', weight: 1 },
    { name: 'customer.addressLine2', weight: 1 },
    { name: 'customer.city', weight: 1 },
    { name: 'financeStatus', weight: 1 },
    { name: 'price', weight: 1 },
    { name: 'title', weight: 1 },
    { name: 'description', weight: 1 },
    { name: 'status', weight: 1 },
    { name: 'id', weight: 1 },
  ],
  includeMatches: true,
  matchAllOnEmptyQuery: true,
  minMatchCharLength: 4,
  threshold: 0.2,
  distance: 1000,
  ignoreLocation: true,
};

export const LEGACY_FEES: StringIndexedObject<number> = {
  INTEREST_FREE_10: 750,
  INTEREST_FREE_12: 835,
  INTEREST_FREE_24: 1250,
  INTEREST_FREE_36: 1800,
  INTEREST_BEARING_12_490: 625,
  INTEREST_BEARING_24_490: 945,
  INTEREST_BEARING_36_490: 1245,
  INTEREST_BEARING_48_490: 1545,
  INTEREST_BEARING_60_490: 1745,
  INTEREST_BEARING_12_790: 475,
  INTEREST_BEARING_24_790: 665,
  INTEREST_BEARING_36_790: 845,
  INTEREST_BEARING_48_790: 1025,
  INTEREST_BEARING_60_790: 1200,
  INTEREST_BEARING_12_990: 400,
  INTEREST_BEARING_24_990: 500,
  INTEREST_BEARING_36_990: 600,
  INTEREST_BEARING_48_990: 700,
  INTEREST_BEARING_60_990: 800,
  INTEREST_BEARING_12_1190: 0,
  INTEREST_BEARING_24_1190: 0,
  INTEREST_BEARING_36_1190: 0,
  INTEREST_BEARING_48_1190: 0,
  INTEREST_BEARING_60_1190: 0,
  INTEREST_BEARING_84_1190: 0,
  INTEREST_BEARING_120_1190: 0,
};
